import React from "react";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Salad, Wheat, Leaf, Ban } from "lucide-react";
import { translations } from "@/lib/translations";

interface DietaryPreference {
  id: string;
  label: string;
  icon: React.ReactNode;
  description: string;
}

interface DietaryPreferencesProps {
  preferences?: {
    vegan: boolean;
    vegetarian: boolean;
    glutenFree: boolean;
    none: boolean;
  };
  onPreferenceChange?: (key: string, value: boolean) => void;
}

export default function DietaryPreferences({
  preferences = {
    vegan: false,
    vegetarian: false,
    glutenFree: false,
    none: true,
  },
  onPreferenceChange = () => {},
}: DietaryPreferencesProps) {
  const dietaryOptions: DietaryPreference[] = [
    {
      id: "vegan",
      label: translations.mealPlanner.dietary.vegan,
      icon: <Leaf className="h-6 w-6 text-green-600" />,
      description: translations.mealPlanner.dietary.veganDesc,
    },
    {
      id: "vegetarian",
      label: translations.mealPlanner.dietary.vegetarian,
      icon: <Salad className="h-6 w-6 text-green-500" />,
      description: translations.mealPlanner.dietary.vegetarianDesc,
    },
    {
      id: "glutenFree",
      label: translations.mealPlanner.dietary.glutenFree,
      icon: <Wheat className="h-6 w-6 text-amber-600" />,
      description: translations.mealPlanner.dietary.glutenFreeDesc,
    },
    {
      id: "none",
      label: translations.mealPlanner.dietary.none,
      icon: <Ban className="h-6 w-6 text-gray-500" />,
      description: translations.mealPlanner.dietary.noneDesc,
    },
  ];

  return (
    <div className="w-full h-full bg-white p-6 rounded-lg">
      <h2 className="text-2xl font-semibold mb-6 text-right">
        {translations.mealPlanner.dietary.title}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {dietaryOptions.map((option) => (
          <Card key={option.id} className="p-6">
            <div className="flex flex-row-reverse items-start gap-4">
              <div className="flex-shrink-0">{option.icon}</div>
              <div className="flex-grow space-y-2">
                <div className="flex items-center justify-between">
                  <Switch
                    id={option.id}
                    checked={preferences[option.id as keyof typeof preferences]}
                    onCheckedChange={(checked) =>
                      onPreferenceChange(option.id, checked)
                    }
                  />
                  <Label
                    htmlFor={option.id}
                    className="text-lg font-medium text-right"
                  >
                    {option.label}
                  </Label>
                </div>
                <p className="text-sm text-gray-500 text-right">
                  {option.description}
                </p>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
